/* eslint-disable */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { Grid, Paper, Typography, Box } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import esLocale from "date-fns/locale/es";
import LocalMachineService from "services/localMachineService";
import VisitTransactionService from "services/visitTransactionService";
import CancelButton from "components/common/CancelButton";
import AcceptButton from "components/common/AcceptButton";
import AppFormContext from "components/common/AppFormContext";
import AppNumberInput from "components/common/AppNumberInput";
import {
  dateOnlyFromDateTime,
  getDefaultOperationDate,
  isBeforeMinOperationDate,
  isFutureOperationDate,
  isValidOperationDate,
  parseDateOnlyToLocalDate,
  toLocalDateOnlyString,
} from "utils/operationDate";

class EditLocalDisplayNumberForm extends Component {
  constructor(props) {
    super(props);
    const current = props.localMachine?.LocalDisplayNumber;
    this.state = {
      localDisplayNumber: current == null ? "" : String(current),
      effectiveDate: getDefaultOperationDate(),
      lastMachineCollectionDate: null,
      dateBounds: null,
      isSaving: false,
      generalError: "",
    };
  }

  async componentDidMount() {
    try {
      const localMachineId = this.props.localMachine?.Id;
      const [lastCollection, dateBounds] = localMachineId
        ? await Promise.all([
            VisitTransactionService.getLastCollectionByMachineInLocal(localMachineId),
            LocalMachineService.getMoveDateBounds(localMachineId),
          ])
        : [null, null];
      const lastMachineCollectionDate = parseDateOnlyToLocalDate(
        dateOnlyFromDateTime(lastCollection?.visit?.DateTime)
      );
      const boundsMinDate = parseDateOnlyToLocalDate(dateBounds?.MinEffectiveDate);
      const effectiveDate =
        boundsMinDate && isBeforeMinOperationDate(this.state.effectiveDate, boundsMinDate)
          ? boundsMinDate
          : this.state.effectiveDate;
      this.setState({ lastMachineCollectionDate, dateBounds, effectiveDate });
    } catch (error) {
      console.error("Error obteniendo la última recaudación de la máquina:", error);
    }
  }

  getLocalDisplayNumberErrorMessage = () => {
    const raw = this.state.localDisplayNumber;
    if (raw === undefined || raw === null || String(raw).trim() === "") return "";
    if (!/^\d+$/.test(String(raw).trim())) {
      return "El Nº en local debe ser un número entero positivo.";
    }
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) {
      return "El Nº en local debe ser un número entero positivo.";
    }
    return "";
  };

  getMinEffectiveDate = () => {
    const startDate = parseDateOnlyToLocalDate(
      dateOnlyFromDateTime(this.props.localMachine?.StartDateTime)
    );
    const lastMachineCollectionDate = this.state.lastMachineCollectionDate;
    const boundsMinDate = parseDateOnlyToLocalDate(
      this.state.dateBounds?.MinEffectiveDate
    );

    const dates = [startDate, lastMachineCollectionDate, boundsMinDate].filter(Boolean);
    if (!dates.length) return null;
    return dates.reduce((latest, current) =>
      current.getTime() >= latest.getTime() ? current : latest
    );
  };

  getEffectiveDateErrorMessage = () => {
    const { effectiveDate, lastMachineCollectionDate } = this.state;
    const minDate = this.getMinEffectiveDate();

    if (!isValidOperationDate(effectiveDate)) {
      return "Selecciona una fecha válida.";
    }
    if (isFutureOperationDate(effectiveDate)) {
      return "No se puede seleccionar una fecha futura.";
    }
    if (
      lastMachineCollectionDate &&
      isBeforeMinOperationDate(effectiveDate, lastMachineCollectionDate)
    ) {
      return "La fecha de edición no puede ser anterior a la última recaudación de esta máquina.";
    }
    if (minDate && isBeforeMinOperationDate(effectiveDate, minDate)) {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    return "";
  };

  getApiErrorMessage = (error, fallback) => {
    const status = error?.response?.status;
    const apiError = error?.response?.data?.error || fallback;
    const apiCode = error?.response?.data?.code || "";

    if (status === 400 && apiCode === "LOCAL_DISPLAY_NUMBER_INVALID") {
      return "El Nº en local debe ser un número entero positivo.";
    }
    if (status === 409 && apiCode === "LOCAL_DISPLAY_NUMBER_DUPLICATE") {
      return "Ya existe una máquina activa con ese Nº en este local.";
    }
    if (status === 400 && apiCode === "LOCAL_DISPLAY_NUMBER_ONLY_FOR_COMERCIO") {
      return "El Nº en local solo aplica a locales comerciales.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_MACHINE_CREATION") {
      return "La fecha seleccionada no puede ser anterior a la fecha de creación de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_CURRENT_LOCAL_MACHINE_START") {
      return "La fecha seleccionada no puede ser anterior al último movimiento de la máquina.";
    }
    if (status === 409 && apiCode === "EFFECTIVE_DATE_BEFORE_LAST_MACHINE_COLLECTION") {
      return "La fecha de edición no puede ser anterior a la última recaudación de esta máquina.";
    }
    if (status === 409 && apiCode === "LOCAL_DISPLAY_NUMBER_DATE_BEFORE_COUNTER_CONFIG") {
      return "La fecha seleccionada no puede ser anterior al último cambio de contadores de esta instalación.";
    }
    if (status === 400 && apiCode === "EFFECTIVE_DATE_CANNOT_BE_FUTURE") {
      return "No se puede seleccionar una fecha futura.";
    }

    return apiError || fallback;
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    const numberError = this.getLocalDisplayNumberErrorMessage();
    const dateError = this.getEffectiveDateErrorMessage();

    if (numberError || dateError) {
      return;
    }

    try {
      this.setState({ isSaving: true, generalError: "" });
      const raw = this.state.localDisplayNumber;
      await LocalMachineService.updateLocalDisplayNumber(this.props.localMachine.Id, {
        LocalDisplayNumber:
          raw === undefined || raw === null || String(raw).trim() === ""
            ? null
            : Number(raw),
        EffectiveDate: toLocalDateOnlyString(this.state.effectiveDate),
      });
      this.props.onSuccess?.();
    } catch (error) {
      console.error("Error actualizando Nº en local:", error);
      this.setState({
        isSaving: false,
        generalError: this.getApiErrorMessage(
          error,
          "No se pudo actualizar el Nº en local."
        ),
      });
    }
  };

  render() {
    const { localMachine, onClose } = this.props;
    const { localDisplayNumber, effectiveDate, isSaving, generalError } = this.state;
    const machineName = localMachine?.Machine?.Name || "Sin nombre";
    const currentNumber = localMachine?.LocalDisplayNumber ?? null;
    const numberError = this.getLocalDisplayNumberErrorMessage();
    const dateError = this.getEffectiveDateErrorMessage();
    const minDate = this.getMinEffectiveDate();
    const today = getDefaultOperationDate();

    return (
      <Paper sx={{ width: "100%", minWidth: 0 }} elevation={0}>
        <form onSubmit={this.handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <AppFormContext
                title={machineName}
                lines={[`Nº actual: ${currentNumber == null ? "Sin Nº" : `Nº ${currentNumber}`}`]}
              />
            </Grid>

            <Grid item xs={12}>
              <AppNumberInput
                fullWidth
                label="Nuevo Nº en local"
                value={localDisplayNumber}
                onValueChange={(value) =>
                  this.setState({ localDisplayNumber: value, generalError: "" })
                }
                decimalScale={0}
                allowNegative={false}
                min={1}
                error={Boolean(numberError)}
                helperText={numberError || "Vacío para dejar sin Nº"}
              />
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
                <DatePicker
                  label="Fecha de modificación"
                  value={effectiveDate}
                  onChange={(date) => this.setState({ effectiveDate: date, generalError: "" })}
                  minDate={minDate || undefined}
                  maxDate={today}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      error: Boolean(dateError),
                      helperText: dateError,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            {generalError && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {generalError}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={1.5} sx={{ width: "100%", "& > button": { flex: 1, minWidth: 0 } }}>
                <CancelButton onClick={onClose} disabled={isSaving} fullWidth>
                  Cancelar
                </CancelButton>
                <AcceptButton
                  type="submit"
                  disabled={isSaving || Boolean(numberError) || Boolean(dateError)}
                  fullWidth
                >
                  {isSaving ? "Guardando..." : "Guardar cambios"}
                </AcceptButton>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    );
  }
}

EditLocalDisplayNumberForm.propTypes = {
  localMachine: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default EditLocalDisplayNumberForm;
